(ns app.front.view.group.admin.party-role-config
  "Allows the authority admin to configure the available party roles.
  Some roles remain hardcoded since they have specific functionality tied to them,
  e.g. applicants and property-owners."
  (:require [app.domain.group.config.model :as config-model]
            [app.domain.util :as du]
            [app.front.entities :as entities]
            [app.front.ui :as ui]
            [app.front.ui.column-select :as ui-column-select]
            [app.front.ui.i18n :as ui-i18n]
            [app.front.ui.tag-type-editor :as ui-tag-type-editor]
            [app.front.view.group.admin.common :as acommon]
            [app.front.view.group.admin.subs :as gas]
            [app.front.view.party.common :as party-common]
            [app.i18n.model :as i18n-model]
            [clojure.string :as str]
            [core.front.event :refer [<sub >evt]]
            [core.front.icons :as i]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]))


;;; Subs
;;;;;;;;;;;;;;;;


(rf/reg-sub ::data :-> ::data)


(rf/reg-sub ::party-role-items
  :<- [::gas/party-roles]
  :<- [:language]
  :<- [::gas/group-tr]
  (fn [[party-roles lang tr] _]
    (->> party-roles
         (sort-by party-common/party-role-label)
         (mapv (fn [entity]
                 (let [trade (:party-role/trade entity)]
                   {:alphabet   (-> entity party-common/party-role-label str str/upper-case first)
                    :id         (:db/id entity)
                    :names      (-> entity :party-role/names i18n-model/strings-map)
                    :hardcoded? (:party-role/hardcoded? entity)
                    :key        (:party-role/key entity)
                    :trade      (when trade
                                  {:name (->> [(when-not (du/is-active? trade)
                                                 (tr [:contractor-registration/obsolete]))
                                               (config-model/localized-name trade lang)]
                                              (remove nil?)
                                              (str/join ": "))
                                   :id   (:db/id trade)})}))))))


(rf/reg-sub ::create-party-role-in-progress?
  :<- [::data]
  :-> :create-in-progress?)


;;; Events
;;;;;;;;;;;;;;;;

(rf/reg-event-fx ::create-party-role.success
  (fn [{:keys [db]} [_ on-done status response]]
    (on-done)
    {:db         (p/dissoc-in db [::data :create-in-progress?])
     :dispatch-n [[::entities/apply-changes status response]
                  [:notifications/add :toast :success :success :party-role-editor/update-success]]}))


(rf/reg-event-fx ::create-party-role.error
  (fn [{:keys [db]} [_ _ {:keys [error]}]]
    {:db       (p/dissoc-in db [::data :create-in-progress?])
     :dispatch [:notifications/add :toast :error :error/error error]}))


(rf/reg-event-fx ::create-party-role
  (fn [{:keys [db]} [_ group-id {:keys [names on-done]}]]
    {:db           (assoc-in db [::data :create-in-progress?] true)
     :comm/command {:command    :group/create-party-role
                    :method     :post
                    :data       {:group-id group-id
                                 :names    names}
                    :on-success [::create-party-role.success on-done]
                    :on-done    [::create-party-role.error]}}))


(rf/reg-event-fx ::delete-party-role
  (fn [_ [_ id]]
    {:comm/command {:command    :group/delete-party-role
                    :method     :post
                    :data       {:role-id id}
                    :on-success [::delete-party-role.success]}}))


(rf/reg-event-fx ::delete-party-role.success
  (fn [_ [_ status response]]
    {:dispatch-n [[::entities/apply-changes status response]
                  [:notifications/add :toast :success :success :party-role-editor/remove-success]]}))


(rf/reg-event-fx ::update-party-role
  (fn [_ [_ command data]]
    {:comm/command {:command    command
                    :method     :post
                    :data       data
                    :on-success [::update-party-role.success]}}))


(rf/reg-event-fx ::update-party-role.success
  (fn [_ [_ status response]]
    {:dispatch-n [[::entities/merge-entity-db status response]
                  [:notifications/add :toast :success :success :party-role-editor/update-success]]}))

(rf/reg-event-fx ::set-trade
  (fn [{:keys []} [_ role-id trade-id]]
    {:comm/command {:command    :group/link-trade-to-party-role
                    :method     :post
                    :data       (util/mmap role-id trade-id)
                    :notify     {:success :party-role-editor/assign-trade-success}
                    :on-success [::entities/apply-changes]}}))

(rf/reg-sub ::trades
  :<- [::gas/config-defaults]
  :<- [::entities/context]
  (fn [[{:keys [trades]} ctx] _]
    (entities/find-entities ctx trades)))

(rf/reg-sub ::trades-as-options
  :<- [::trades]
  :<- [:language]
  (fn [[trades lang] [_ selected-id]]
    (->> trades
         (filter du/is-active?)
         (map (fn [{:db/keys [id] :as trade-config-entity}]
                (util/mmap {:title     (config-model/localized-name trade-config-entity lang)
                            :selected? (= selected-id id)}
                           id)))
         (sort-by :title))))

(rf/reg-sub ::has-trades?
  :<- [::trades]
  :-> (comp some? seq))

(rf/reg-sub ::role-name
  (fn [[_ role-id]]
    [(rf/subscribe [:language])
     (rf/subscribe [::entities/entity-attr role-id :party-role/names])])
  (fn [[lang names] _]
    (i18n-model/get-text-from-entities-or-map names lang)))

;;; Components
;;;;;;;;;;;;;;;;

(defn trade-modal [{:keys [tr *trade-selection-ctx]}]
  (let [{:keys [role-id
                selected-trade-id
                initial-trade-id]} @*trade-selection-ctx
        role-name                  (<sub [::role-name role-id])]
    [ui/confirm-modal
     (util/mmap {:open?           (some? role-id)
                 :yes-disabled?   (= selected-trade-id initial-trade-id)
                 :on-yes-click    #(>evt [::set-trade role-id selected-trade-id])
                 :modal-title     (tr [:party-role-editor/assign-trade-title] [role-name])
                 :on-close        #(reset! *trade-selection-ctx nil)
                 :yes-button-text (tr [:party-role-editor/set-trade-assignment])
                 :no-button-text  (tr [:cancel])}
                tr)
     [ui-column-select/column-select
      (util/mmap {:options      (cons {:id        nil
                                       :title     (tr [:none])
                                       :selected? (nil? selected-trade-id)}
                                      (<sub [::trades-as-options selected-trade-id]))
                  :transparent? true
                  :title        (tr [:party-role-editor/available-trades])
                  :on-change    #(swap! *trade-selection-ctx assoc :selected-trade-id %)}
                 tr)]]))

(defn party-roles-editor
  "Note that unlike e.g. the attachment type editor we do not select/unselect roles here;
  applicable roles are configured elsewhere together with the required roles"
  [tr group-id]
  (r/with-let [*trade-selection-ctx (r/atom nil)]
    (let [party-roles  (<sub [::party-role-items])
          language     (<sub [::ui-i18n/editor-language ::gas/allowed-locales])
          role-in-use? (<sub [::gas/roles-in-use])]
      [:<>
       [trade-modal (util/mmap tr *trade-selection-ctx)]
       [ui-tag-type-editor/editor
        (-> {:tr                  tr
             :values              party-roles
             :on-end-edit         (fn [{:keys [id names]}]
                                    (let [language (ui-i18n/get-actual-language-to-edit names language)]
                                      (>evt [::update-party-role
                                             :group/rename-party-role
                                             {:role-id  id
                                              :language language
                                              :name     (get names language)}])))
             :edit-disabled?      #(or (:hardcoded? %)
                                       (-> % :id role-in-use?))
             :group-by-fn         :alphabet
             :name-attr           :names
             :on-create           #(>evt [::create-party-role group-id %])
             :on-delete           #(>evt [::delete-party-role (:id %)])
             :create-in-progress? (<sub [::create-party-role-in-progress?])
             :tag-type-name       (tr [:party-role-editor/party-role])
             :remove-confirmation (tr [:party-role-editor/remove-confirmation])
             :additional-info-fn  (fn [{{:keys [name]} :trade}]
                                    name)}
            (p/assoc-if :actions-fn (when (<sub [::has-trades?])
                                      (fn [role]
                                        [{:title    (tr [:party-role-editor/assign-trade])
                                          :on-click #(reset! *trade-selection-ctx
                                                             {:role-id           (:id role)
                                                              :initial-trade-id  (-> role :trade :id)
                                                              :selected-trade-id (-> role :trade :id)})
                                          :icon     i/link}]))))]])))


(defn party-role-config-section [group-id & [section-options]]
  (let [tr (<sub [::gas/group-tr])]
    [acommon/config-section
     (tr [:group-admin/party-roles-title])
     (tr [:group-admin/party-roles-instruction])
     section-options
     [party-roles-editor tr group-id]]))
